import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  SearchBarFilterOption,
  SearchBarQueryPayload,
  SearchFilterCondition,
  SearchFilterFieldSchema,
  SearchFilterOperator,
  SearchFilterSchema,
  SearchOperatorOption,
  SearchRangeState,
} from './search-bar.types';

@Component({
  selector: 'app-search-bar',
  standalone: false,
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
})
export class SearchBarComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() searchFilterSchema: SearchFilterSchema | null = null;
  @Input() loading = false;
  @Input() placeholder = '';
  @Input() primaryFilterKeys: string[] = [];
  inlineFilterCount = 0;
  moreFiltersOpen = false;
  private resizeObserver?: ResizeObserver;

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly cdr: ChangeDetectorRef) {}

  toolbarWidth = 0;

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.updateToolbarLayout());
    this.resizeObserver.observe(this.host.nativeElement);
    this.updateToolbarLayout();
  }

  @HostListener('window:resize')
  updateToolbarLayout(): void {
    const element = this.host.nativeElement;
    // The app shell intentionally has a 1000px canvas. Keep SearchBar controls
    // reachable in the visible viewport without changing that shell contract.
    const width = Math.max(80, Math.min(element.clientWidth,
      window.innerWidth - Math.max(0, element.getBoundingClientRect().left) - 16));
    const searchWidth = width < 576 ? Math.min(140, width / 2) : 220;
    const count = Math.max(0, Math.min(4, Math.floor((width - searchWidth - 140) / 175)));
    if (count !== this.inlineFilterCount || Math.abs(width - this.toolbarWidth) > 0.5) {
      this.toolbarWidth = width;
      this.inlineFilterCount = count;
      this.cdr.detectChanges();
    }
  }

  private get orderedInlineFields(): SearchFilterFieldSchema[] {
    return this.filterableFields.filter(field => field.datatype !== 'search' && field.datatype !== 'boolean')
      .sort((a, b) => {
        const rank = (key: string) => this.primaryFilterKeys.includes(key) ? this.primaryFilterKeys.indexOf(key) : this.primaryFilterKeys.length;
        return rank(a.key) - rank(b.key);
      }).slice(0, this.inlineFilterCount);
  }

  get visibleOptionFilters(): SearchFilterFieldSchema[] {
    return this.orderedInlineFields.filter(field => field.datatype === 'option');
  }

  get visibleComparisonFilters(): SearchFilterFieldSchema[] {
    return this.orderedInlineFields.filter(field => field.datatype === 'date' || field.datatype === 'number');
  }

  get overflowFilters(): SearchFilterFieldSchema[] {
    const visible = new Set(this.orderedInlineFields.map(field => field.key));
    return this.filterableFields.filter(field => field.datatype !== 'search' && !visible.has(field.key))
      .sort((a, b) => Number(b.datatype === 'boolean') - Number(a.datatype === 'boolean'));
  }

  toggleBoolean(field: SearchFilterFieldSchema, checked: boolean): void {
    this.selectedOptions[field.key] = [String(checked)];
    this.queueAutoApply(0);
  }

  toggleMoreFilters(event: MouseEvent): void {
    event.stopPropagation();
    this.openDropdownKey = null;
    this.moreFiltersOpen = !this.moreFiltersOpen;
  }

  @Output() queryApplied = new EventEmitter<SearchBarQueryPayload>();

  @Input() searchText = '';

  /**
   * For option/multi-select filters.
   *
   * Example:
   * {
   *   departments_id: ['uuid-1', 'uuid-2']
   * }
   */
  selectedOptions: Record<string, string[]> = {};

  /**
   * Search text inside option dropdowns.
   */
  optionSearchText: Record<string, string> = {};

  /**
   * For number/date comparison filters.
   */
  rangeValues: Record<string, SearchRangeState> = {};

  /**
   * Current open dropdown key.
   */
  openDropdownKey: string | null = null;

  numberOperators: SearchOperatorOption[] = [
    { value: 'eq', label: 'Equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'between', label: 'Between' },
  ];

  dateOperators: SearchOperatorOption[] = [
    { value: 'eq', label: 'On' },
    { value: 'gt', label: 'After' },
    { value: 'gte', label: 'On or after' },
    { value: 'lt', label: 'Before' },
    { value: 'lte', label: 'On or before' },
    { value: 'between', label: 'Between' },
  ];

  ngOnInit(): void {
    this.prepareStateFromSchema();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchFilterSchema']) {
      const previous = changes['searchFilterSchema'].previousValue as SearchFilterSchema | null;
      const previousFields = new Map((previous?.fields || previous?.filters || []).map(field => [field.key,field]));
      for(const field of this.searchFilterSchema?.fields || this.searchFilterSchema?.filters || []) {
        const previousField = previousFields.get(field.key);
        if (previousField?.defaultValue !== field.defaultValue || previousField?.initialValue !== field.initialValue
          || previousField?.initialRange?.from !== field.initialRange?.from
          || previousField?.initialRange?.to !== field.initialRange?.to) {
          const value = field.initialValue !== undefined ? field.initialValue : field.defaultValue;
          if (field.datatype === 'option' || field.datatype === 'boolean') this.selectedOptions[field.key] = value ? [value] : [];
          else if (field.datatype === 'date' || field.datatype === 'number') this.rangeValues[field.key] = this.createInitialRangeState(field);
        }
      }
      this.prepareStateFromSchema();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.autoApplyTimer) {
      clearTimeout(this.autoApplyTimer);
      this.autoApplyTimer = null;
    }
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.openDropdownKey = null;
    this.moreFiltersOpen = false;
  }

  get schemaFields(): SearchFilterFieldSchema[] {
    return this.searchFilterSchema?.fields ?? this.searchFilterSchema?.filters ?? [];
  }

  get filterableFields(): SearchFilterFieldSchema[] {
    return this.schemaFields.filter((field) => field.filter !== false);
  }

  get searchFields(): SearchFilterFieldSchema[] {
    return this.filterableFields.filter((field) => field.datatype === 'search');
  }

  get optionFilters(): SearchFilterFieldSchema[] {
    return this.filterableFields.filter(
      (field) => field.datatype === 'option' || field.datatype === 'boolean',
    );
  }

  get comparisonFilters(): SearchFilterFieldSchema[] {
    return this.filterableFields.filter((field) => {
      return field.datatype === 'number' || field.datatype === 'date';
    });
  }

  get activeFilterCount(): number {
    let count = 0;

    if (this.searchText.trim()) {
      count++;
    }

    for (const field of this.optionFilters) {
      if (this.isOptionFilterActive(field)) {
        count++;
      }
    }

    for (const field of this.comparisonFilters) {
      if (this.hasRangeValue(field)) {
        count++;
      }
    }

    return count;
  }

  get hasFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  apply(): void {
    // Enter commits the current text before another table action can replace URL state.
    if (this.autoApplyTimer) {
      clearTimeout(this.autoApplyTimer);
      this.autoApplyTimer = null;
    }
    if (!this.searchFilterSchema) {
      return;
    }

    const payload: SearchBarQueryPayload = {
      table: this.searchFilterSchema.table,
      query: this.searchText.trim() || null,

      /**
       * Send field keys instead of raw DB columns.
       * This avoids ambiguity when joined tables have common columns like "id" or "name".
       *
       * Backend query builder supports matching by key or column.
       */
      searchColumns: this.searchFields.map((field) => field.key),

      filters: this.buildFilterConditions(),
      // Sorting belongs to sortable table headers, not the search control.
      sort: null,
    };

    this.queryApplied.emit(payload);
  }

  clear(): void {
    this.searchText = '';

    for (const field of this.optionFilters) {
      this.selectedOptions[field.key] = field.defaultValue ? [field.defaultValue] : [];
      this.optionSearchText[field.key] = '';
    }

    for (const field of this.comparisonFilters) {
      this.rangeValues[field.key] = this.createDefaultRangeState(field);
    }

    this.openDropdownKey = null;
    this.apply();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.apply();
    }
  }

  toggleDropdown(event: MouseEvent, key: string): void {
    event.stopPropagation();
    this.moreFiltersOpen = false;
    this.openDropdownKey = this.openDropdownKey === key ? null : key;
  }

  keepDropdownOpen(event: MouseEvent): void {
    event.stopPropagation();
  }

  isDropdownOpen(key: string): boolean {
    return this.openDropdownKey === key;
  }

  setDropdownOpen(key: string, open: boolean): void {
    if (open) {
      this.openDropdownKey = key;
      return;
    }

    if (this.openDropdownKey === key) {
      this.openDropdownKey = null;
    }
  }

  getOperatorsForField(field: SearchFilterFieldSchema): SearchOperatorOption[] {
    const operators=field.datatype === 'date' ? this.dateOperators : this.numberOperators;
    return field.allowedOperators ? operators.filter(operator=>field.allowedOperators!.includes(operator.value)) : operators;
  }

  getInputType(field: SearchFilterFieldSchema): 'text' | 'number' | 'date' | 'datetime-local' {
    if (field.inputType) return field.inputType;
    if (field.datatype === 'date') {
      return 'date';
    }

    if (field.datatype === 'number') {
      return 'number';
    }

    return 'text';
  }

  getSelectedOptionValues(field: SearchFilterFieldSchema): string[] {
    return this.selectedOptions[field.key] ?? [];
  }

  isOptionFilterActive(field: SearchFilterFieldSchema): boolean {
    const selected = this.getSelectedOptionValues(field);
    return selected.length > 0 && !(selected.length === 1 && selected[0] === field.defaultValue);
  }

  isOptionSelected(field: SearchFilterFieldSchema, option: SearchBarFilterOption): boolean {
    return this.getSelectedOptionValues(field).includes(option.value);
  }

  toggleOption(
    field: SearchFilterFieldSchema,
    option: SearchBarFilterOption,
    checked: boolean,
  ): void {
    if (field.selectionMode === 'single') {
      this.selectedOptions[field.key] = checked ? [option.value] : [];
      this.queueAutoApply(0);
      return;
    }
    const current = new Set(this.getSelectedOptionValues(field));

    if (checked) {
      current.add(option.value);
    } else {
      current.delete(option.value);
    }

    this.selectedOptions[field.key] = Array.from(current);
    this.queueAutoApply();
  }

  selectAllOptions(field: SearchFilterFieldSchema): void {
    if (field.selectionMode === 'single') return;
    this.selectedOptions[field.key] = this.getFilteredOptions(field).map((option) => option.value);
    this.queueAutoApply(0);
  }

  clearOptions(field: SearchFilterFieldSchema): void {
    this.selectedOptions[field.key] = field.defaultValue ? [field.defaultValue] : [];
    this.queueAutoApply(0);
  }

  getFilteredOptions(field: SearchFilterFieldSchema): SearchBarFilterOption[] {
    const options = this.getFieldOptions(field);
    const search = (this.optionSearchText[field.key] ?? '').trim().toLowerCase();

    if (!search) {
      return options;
    }

    return options.filter((option) => {
      return (
        option.label.toLowerCase().includes(search) ||
        option.value.toLowerCase().includes(search)
      );
    });
  }

  getOptionButtonLabel(field: SearchFilterFieldSchema): string {
    const selected = this.getSelectedOptionValues(field);

    if (selected.length === 0) {
      return field.label;
    }

    if (selected.length === 1) {
      const option = this.getFieldOptions(field).find((item) => item.value === selected[0]);
      return option?.label ?? field.label;
    }

    return `${field.label}: ${selected.length}`;
  }

  getRangeState(field: SearchFilterFieldSchema): SearchRangeState {
    if (!this.rangeValues[field.key]) {
      this.rangeValues[field.key] = this.createDefaultRangeState(field);
    }

    return this.rangeValues[field.key];
  }

  setRangeOperator(field: SearchFilterFieldSchema, operator: SearchFilterOperator): void {
    this.getRangeState(field).operator = operator;
    this.queueAutoApply();
  }

  setRangeValue(field: SearchFilterFieldSchema, value: string): void {
    this.getRangeState(field).value = value;
    this.queueAutoApply();
  }

  setRangeFrom(field: SearchFilterFieldSchema, value: string): void {
    this.getRangeState(field).from = value;
    this.queueAutoApply();
  }

  setRangeTo(field: SearchFilterFieldSchema, value: string): void {
    this.getRangeState(field).to = value;
    this.queueAutoApply();
  }

  getRangeButtonLabel(field: SearchFilterFieldSchema): string {
    const state = this.getRangeState(field);

    if (!this.hasRangeValue(field)) {
      return field.label;
    }

    const operatorLabel =
      this.getOperatorsForField(field).find((operator) => operator.value === state.operator)
        ?.label ?? state.operator;

    if (state.operator === 'between') {
      if (!state.to) return `${field.label}: From ${state.from}`;
      if (!state.from) return `${field.label}: Through ${state.to}`;
      return `${field.label}: ${state.from} → ${state.to}`;
    }

    return `${field.label}: ${operatorLabel} ${state.value}`;
  }

  hasRangeValue(field: SearchFilterFieldSchema): boolean {
    const state = this.getRangeState(field);

    if (state.operator === 'between') {
      if (field.allowOpenRange ? !state.from && !state.to : !state.from || !state.to) return false;
      return !this.isInvalidRange(field);
    }

    return Boolean(state.value);
  }

  isInvalidRange(field: SearchFilterFieldSchema): boolean {
    const state = this.getRangeState(field);
    if (state.operator !== 'between' || !state.from || !state.to) return false;
    return field.datatype === 'number'
      ? Number(state.from) > Number(state.to)
      : state.from > state.to;
  }

  clearRange(field: SearchFilterFieldSchema): void {
    this.rangeValues[field.key] = this.createDefaultRangeState(field);
    this.queueAutoApply(0);
  }

  onSearchTextChange(value: string): void {
    this.searchText = value;
    this.queueAutoApply(400);
  }

  trackByFieldKey(_: number, field: SearchFilterFieldSchema): string {
    return field.key;
  }

  trackByOptionValue(_: number, option: SearchBarFilterOption): string {
    return option.value;
  }

  private prepareStateFromSchema(): void {
    for (const field of this.optionFilters) {
      if (!this.selectedOptions[field.key]) {
        const value = field.initialValue !== undefined ? field.initialValue : field.defaultValue;
        this.selectedOptions[field.key] = value ? [value] : [];
      }

      if (!this.optionSearchText[field.key]) {
        this.optionSearchText[field.key] = '';
      }
    }

    for (const field of this.comparisonFilters) {
      if (!this.rangeValues[field.key]) {
        this.rangeValues[field.key] = this.createInitialRangeState(field);
      }
    }
  }

  private getFieldOptions(field: SearchFilterFieldSchema): SearchBarFilterOption[] {
    if (field.options?.length) {
      return field.options;
    }

    if (field.datatype === 'boolean') {
      return [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ];
    }

    return [];
  }

  private createInitialRangeState(field: SearchFilterFieldSchema): SearchRangeState {
    return {
      ...this.createDefaultRangeState(field),
      value: (field.initialValue !== undefined ? field.initialValue : field.defaultValue) || '',
      from: field.initialRange?.from || '',
      to: field.initialRange?.to || '',
    };
  }

  private createDefaultRangeState(field: SearchFilterFieldSchema): SearchRangeState {
    return {
      operator: field.defaultOperator ?? (field.datatype === 'date' || field.datatype === 'number' ? 'between' : 'eq'),
      value: field.defaultValue || '',
      from: '',
      to: '',
    };
  }

  private autoApplyTimer: ReturnType<typeof setTimeout> | null = null;

  private queueAutoApply(delay = 200): void {
    if (this.autoApplyTimer) {
      clearTimeout(this.autoApplyTimer);
    }

    this.autoApplyTimer = setTimeout(() => {
      this.autoApplyTimer = null;
      this.apply();
    }, delay);
  }

  private buildFilterConditions(): SearchFilterCondition[] {
    const filters: SearchFilterCondition[] = [];

    for (const field of this.optionFilters) {
      const selectedValues = this.getSelectedOptionValues(field);

      if (selectedValues.length === 0) {
        continue;
      }

      filters.push({
        key: field.key,
        label: field.label,

        /**
         * Use key here to avoid ambiguity.
         * Backend can resolve by key first, then by column.
         */
        column: field.key,

        datatype: field.datatype,
        operator: 'in',
        value: selectedValues,
      });
    }

    for (const field of this.comparisonFilters) {
      const state = this.getRangeState(field);

      if (!this.hasRangeValue(field)) {
        continue;
      }

      filters.push({
        key: field.key,
        label: field.label,
        column: field.key,
        datatype: field.datatype,
        operator: state.operator,
        value:
          state.operator === 'between'
            ? {
                from: state.from,
                to: state.to,
              }
            : state.value,
      });
    }

    return filters;
  }
}
